/**
 * Manages automatic whitespace trimming for default slots in custom elements.
 */
export class SlotWhitespaceTrimmer {
  #cleanupListeners: Array<() => void> = [];
  #pruneTimeoutId: ReturnType<typeof setTimeout> | null = null;
  #observer?: MutationObserver;

  constructor(
    private readonly host: HTMLElement,
    private readonly shadowRoot: ShadowRoot,
  ) {}

  init(): void {
    const defaultSlots = [...this.shadowRoot.querySelectorAll<HTMLSlotElement>("slot:not([name])")];

    if (defaultSlots.length === 0) return;

    const prune = this.#createPruneFunction(defaultSlots);
    prune();
    this.#setupSlotListeners(defaultSlots, prune);

    this.#observer = new MutationObserver(prune);
    this.#observer.observe(this.host, { childList: true, characterData: true });
  }

  destroy(): void {
    if (this.#pruneTimeoutId !== null) {
      clearTimeout(this.#pruneTimeoutId);
      this.#pruneTimeoutId = null;
    }

    this.#observer?.disconnect();
    this.#observer = undefined;
    for (const off of this.#cleanupListeners) off();
    this.#cleanupListeners = [];
  }

  readonly #createPruneFunction = (defaultSlots: HTMLSlotElement[]): (() => void) => {
    return () => {
      if (this.#pruneTimeoutId) return;
      this.#pruneTimeoutId = setTimeout(() => {
        this.#pruneTimeoutId = null;
        this.#pruneWhitespaceNodes(defaultSlots);
      }, 0);
    };
  };

  readonly #pruneWhitespaceNodes = (defaultSlots: HTMLSlotElement[]): void => {
    const assignedNodes = defaultSlots.flatMap((s) => s.assignedNodes({ flatten: true }));
    const hasAssignedElement = assignedNodes.some((n) => n.nodeType === Node.ELEMENT_NODE);
    if (hasAssignedElement) return;

    const toRemove: ChildNode[] = [];
    for (const n of this.host.childNodes) {
      if (n.nodeType === Node.TEXT_NODE && !n.textContent?.trim()) {
        toRemove.push(n);
      }
    }
    for (const n of toRemove) {
      if (n.parentNode === this.host) n.remove();
    }
  };

  readonly #setupSlotListeners = (defaultSlots: HTMLSlotElement[], prune: () => void): void => {
    for (const s of defaultSlots) {
      s.addEventListener("slotchange", prune);
      this.#cleanupListeners.push(() => s.removeEventListener("slotchange", prune));
    }
  };
}
